import {
    memo,
    type SyntheticEvent,
    type PropsWithChildren
} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import { getFormInputProps } from "util/forms";
import { getOptionName } from "util/option";
import { when } from "util/support";
import JsonEditor from "lib/jsoneditor";
import { selectConnectorsFlowsOptions } from "features/general/selectors";
import Tabs from "ui/atoms/Tabs";
import { AssetTextField } from "ui/atoms/TextField";
import When from "ui/support/When";
import Loader from "ui/molecules/Loader";
import { BooleanStatus } from "ui/molecules/Status";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { Checkbox } from "ui/forms/formik-elements";
import { FormField, type BankingSDKProps } from "./types";
import { ToggleEnabledOpenBankingResource } from "../shared";

const BankingSDK = ({
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isBootstrapDataLoading,
    bootstrapData,
    children,
    ...restProps
}: PropsWithChildren<BankingSDKProps>) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <When
            if={Boolean(values[FormField.BankId])}
            then={(
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    autoComplete='off'
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: {
                                xs: 2,
                                sm: 3
                            },
                            alignItems: 'flex-start'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    lg: 'repeat(2, 1fr)'
                                },
                                gap: 2
                            }}
                        >
                            <TextField
                                fullWidth
                                disabled
                                size='small'
                                name={FormField.BankId}
                                value={values[FormField.BankId]}
                                label='Bank ID'
                            />
                            <CountryIso2
                                fullWidth
                                disabled
                                disableClearable
                                value={values[FormField.CountryIso]}
                                onChange={onAutocompleteChange(FormField.CountryIso)}
                                TextFieldProps={{
                                    name: FormField.CountryIso,
                                    label: 'Country'
                                }}
                            />
                            <TextField
                                fullWidth
                                disabled
                                size='small'
                                name={FormField.BankGroup}
                                value={values[FormField.BankGroup]}
                                label='Bank Group'
                            />
                            <TextField
                                fullWidth
                                disabled
                                size='small'
                                name={FormField.FullName}
                                value={values[FormField.FullName]}
                                label='Full Name'
                            />
                            <Divider
                                sx={{
                                    gridColumn: 'span 2',
                                    my: 2
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2
                                }}
                            >
                                <AssetTextField
                                    {...getFormInputProps(FormField.LogoUrl, restProps)}
                                    fullWidth
                                    inputProps={{
                                        maxLength: 191
                                    }}
                                    size='small'
                                    name={FormField.LogoUrl}
                                    value={values[FormField.LogoUrl] || ''}
                                    label={`Logo URL (${bootstrapData.OpenBankingLogosDomain})`}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <StatefulSelector
                                    fullWidth
                                    size='small'
                                    layout={null}
                                    loading={isBootstrapDataLoading}
                                    disabled={isBootstrapDataLoading}
                                    selectOptions={selectConnectorsFlowsOptions}
                                    value={values[FormField.Flow]}
                                    getOptionLabel={getOptionName}
                                    onChange={onAutocompleteChange(FormField.Flow)}
                                    TextFieldProps={{
                                        ...getFormInputProps(FormField.Flow, restProps),
                                        name: FormField.Flow,
                                        label: 'Flow'
                                    }}
                                />
                            </Box>
                            <TextField
                                {...getFormInputProps(FormField.Obs, restProps)}
                                fullWidth
                                multiline
                                rows={4}
                                size='small'
                                name={FormField.Obs}
                                value={values[FormField.Obs]}
                                label='Description'
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <Box
                                sx={{
                                    display: 'grid'
                                }}
                            >
                                <ToggleEnabledOpenBankingResource
                                    {...restProps}
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    handleBlur={handleBlur}
                                    CheckboxProps={{
                                        name: FormField.IsEnabled,
                                        checked: values[FormField.IsEnabled]
                                    }}
                                />
                                <Checkbox
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    label='Should Credit On Redirect'
                                    handleBlur={handleBlur}
                                    CheckboxProps={{
                                        inputProps: {
                                            'aria-label': 'Should Credit On Redirect'
                                        },
                                        name: FormField.ShouldCreditOnRedirect,
                                        checked: values[FormField.ShouldCreditOnRedirect]
                                    }}
                                />
                                <Tooltip
                                    arrow
                                    title={when(
                                        values[FormField.Status],
                                        'Production testing is not allowed',
                                        'Allow production testing'
                                    )}
                                    placement='top-start'
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: 'fit-content',
                                            gap: 1,
                                            mt: 2
                                        }}
                                    >
                                        <Typography
                                            variant='subtitle2'
                                            color='text.secondary'
                                        >
                                            Status:
                                        </Typography>
                                        <BooleanStatus
                                            label={!values[FormField.Status]}
                                        />
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Tabs
                            model={[
                                {
                                    label: 'Ais Options',
                                    component: (
                                        <JsonEditor
                                            value={values[FormField.AisOptions]}
                                        />
                                    )
                                },
                                {
                                    label: 'Pis Options',
                                    component: (
                                        <JsonEditor
                                            value={values[FormField.PisOptions]}
                                        />
                                    )
                                }
                            ]}
                            TabListProps={{
                                'aria-label': 'banking sdk details',
                                variant: 'scrollable',
                                scrollButtons: 'auto'
                            }}
                        />
                    </Box>
                    {children}
                </Box>
            )}
        >
            <Loader
                isLoading
                sx={{
                    height: 'calc(100vh - 200px)',
                    display: 'grid',
                    placeContent: 'center'
                }}
            />
        </When>
    );
};

export default memo(BankingSDK);
